/**
 * scrollToAnchor — скролл к секции по id на длинной странице.
 *
 * Зачем не нативный <a href="#id"> и не одиночный scrollTo:
 * главная очень длинная, секции подгружают контент, и цель уезжает уже
 * во время движения. Одиночный smooth вычисляет целевой пиксель в момент
 * клика и встаёт коротко — до формы не доезжает.
 *
 * offset = 72px: 64px sticky-nav + 8px зазор, чтобы заголовок секции не
 * прятался под шапкой.
 */
/**
 * Поколение прокрутки. Каждый вызов и каждое вмешательство человека
 * увеличивают счётчик; анимация и отложенные коррекции сверяются с ним и
 * молча умирают, если они уже не последние.
 *
 * Без этого коррекции прошлого клика (до 2,4 с) жили своей жизнью: кликаешь
 * «ROI», через секунду «Кейсы» — и экран на полпути дёргало обратно к
 * калькулятору. То же с колесом мыши: человек уже листает сам, а его
 * возвращало к блоку.
 */
let generation = 0

export function scrollToAnchor(id: string, offset = 72): void {
  if (typeof document === 'undefined') return

  const gen = ++generation
  const alive = () => gen === generation

  // Человек взял прокрутку в свои руки — отпускаем её сразу.
  const release = () => {
    if (alive()) generation++
  }
  const INPUTS = ['wheel', 'touchstart', 'keydown'] as const
  INPUTS.forEach((e) => window.addEventListener(e, release, { passive: true, once: true }))
  window.setTimeout(() => INPUTS.forEach((e) => window.removeEventListener(e, release)), 2600)

  const computeTop = (): number | null => {
    const el = document.getElementById(id)
    if (!el) return null
    return el.getBoundingClientRect().top + window.scrollY - offset
  }

  /**
   * Своя анимация вместо нативной — ради ПОСТОЯННОЙ длительности.
   *
   * У браузера плавная прокрутка тянется тем дольше, чем длиннее путь:
   * замер на 9100 px показал, что через 4 секунды экран ещё ехал. Я чинил
   * это мгновенным прыжком на дальние цели — и получил обратную жалобу:
   * «переходы по верхнему меню, всё скачет очень быстро». Телепортация
   * через полстраницы дезориентирует не меньше, чем поездка на 4 секунды:
   * человек не понимает, куда его перенесло.
   *
   * Здесь путь занимает ~700 мс независимо от расстояния. Близкая секция
   * подъезжает мягко, дальняя — быстро, но глаз успевает считать, что
   * было движение, а не подмена экрана. Кривая ease-in-out-cubic:
   * трогается и тормозит плавно, середину проходит быстро.
   */
  const DURATION = 700
  let raf = 0

  const reduced = () =>
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const animate = (to: number) => {
    // Человек попросил без движения — переносим сразу, без анимации.
    if (reduced()) {
      window.scrollTo({ top: to, behavior: 'instant' })
      return
    }
    cancelAnimationFrame(raf)
    const from = window.scrollY
    const dist = to - from
    if (Math.abs(dist) < 2) return
    const t0 = performance.now()
    const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)
    const step = (now: number) => {
      if (!alive()) return
      const t = Math.min(1, (now - t0) / DURATION)
      // behavior:'instant' на каждом кадре обязателен: в tokens.css у html
      // стоит scroll-behavior:smooth, и без него браузер начал бы плавно
      // догонять каждый наш шаг — анимация поверх анимации.
      window.scrollTo({ top: from + dist * ease(t), behavior: 'instant' })
      if (t < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
  }

  const snap = (top: number) => {
    cancelAnimationFrame(raf)
    window.scrollTo({ top, behavior: 'instant' })
  }

  // Первая попытка — сразу, анимацией.
  const first = computeTop()
  if (first != null) animate(first)

  // Коррекции после оседания layout: контент догрузился, высоты финальны,
  // цель могла уехать. Только если промахнулись заметно и анимация уже
  // закончилась — иначе дёргали бы её на ходу.
  ;[900, 1600, 2400].forEach((delay) =>
    window.setTimeout(() => {
      if (!alive()) return
      const top = computeTop()
      if (top != null && Math.abs(top - window.scrollY) > 24) snap(top)
    }, delay),
  )
}
